import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";
import { applyTheme, FONT_OPTIONS } from "@/lib/theme";

const COLOR_FIELDS = [
  { key: "burgundy", label: "Primary accent" },
  { key: "burgundyDeep", label: "Primary accent — hover" },
  { key: "champagne", label: "Secondary accent (gold)" },
  { key: "champagneLight", label: "Secondary — light" },
  { key: "black", label: "Header / footer (dark)" },
  { key: "cream", label: "Page background" },
];

export default function CMSPage() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/cms/content").then((r) => setContent(r.data)); }, []);

  const upd = (section, key, val) => setContent({ ...content, [section]: { ...content[section], [key]: val } });
  const updThemeField = (key, val) => setContent({ ...content, theme: { ...(content.theme || {}), [key]: val } });
  const updThemeColor = (key, val) => setContent({
    ...content,
    theme: { ...(content.theme || {}), colors: { ...((content.theme || {}).colors || {}), [key]: val } },
  });

  const previewTheme = (next) => applyTheme(next || content);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cms/content", content);
      applyTheme(content); // reflect colours + fonts instantly
      toast.success("Site content & theme saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!content) return <div className="p-10 text-neutral-500">Loading…</div>;

  const theme = content.theme || {};
  const colors = theme.colors || {};

  return (
    <div data-testid="cms-page">
      <PageHeader title="Public Site CMS" subtitle="IMAGES · COLOURS · FONTS · COPY"
        actions={<button className="k-btn kazo-bg-burgundy" onClick={save} disabled={saving} data-testid="cms-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}</button>} />
      <div className="p-8 space-y-6 max-w-4xl">

        <Section title="BRAND LOGO">
          <p className="text-xs text-neutral-500 -mt-1">Shown in the public header & footer. Upload a PNG (transparent works best) or paste a URL. Leave blank to use the bundled logo.</p>
          <ImageField label="Logo image" value={content.brand?.logo_url || ""} onChange={(v) => upd("brand", "logo_url", v)} testid="cms-logo" />
        </Section>

        <Section title="THEME · COLOURS">
          <p className="text-xs text-neutral-500 -mt-1">Pick your brand colours. Changes preview live; press Save to publish to the public site.</p>
          <div className="grid grid-cols-2 gap-4">
            {COLOR_FIELDS.map((f) => (
              <ColorField key={f.key} label={f.label} value={colors[f.key] || "#000000"}
                onChange={(v) => { updThemeColor(f.key, v); previewTheme({ ...content, theme: { ...theme, colors: { ...colors, [f.key]: v } } }); }}
                testid={`cms-color-${f.key}`} />
            ))}
          </div>
        </Section>

        <Section title="THEME · FONTS">
          <div className="grid grid-cols-2 gap-4">
            <FontSelect label="Headings / display font" value={theme.display_font || "Inter"}
              onChange={(v) => { updThemeField("display_font", v); previewTheme({ ...content, theme: { ...theme, display_font: v } }); }} testid="cms-font-display" />
            <FontSelect label="Body font" value={theme.body_font || "Inter"}
              onChange={(v) => { updThemeField("body_font", v); previewTheme({ ...content, theme: { ...theme, body_font: v } }); }} testid="cms-font-body" />
          </div>
          <div className="mt-3 p-4 border border-black/10 bg-white">
            <div className="editorial-headline text-3xl">The quick brown fox</div>
            <div className="text-sm text-neutral-600 mt-1">Body preview — jumps over the lazy dog. 1234567890</div>
          </div>
        </Section>

        <Section title="ANNOUNCEMENT BAR">
          <Input label="Top bar text" value={content.home?.topbar_text || ""} onChange={(v) => upd("home", "topbar_text", v)} testid="cms-topbar" />
        </Section>

        <Section title="HERO SECTION">
          <Input label="Eyebrow tag" value={content.home?.hero_eyebrow || ""} onChange={(v) => upd("home", "hero_eyebrow", v)} testid="cms-hero-eyebrow" />
          <div className="grid grid-cols-3 gap-3">
            <Input label="Headline part 1" value={content.home?.hero_headline_1 || ""} onChange={(v) => upd("home", "hero_headline_1", v)} testid="cms-h1" />
            <Input label="Italic accent" value={content.home?.hero_headline_em || ""} onChange={(v) => upd("home", "hero_headline_em", v)} testid="cms-h-em" />
            <Input label="Headline part 2" value={content.home?.hero_headline_2 || ""} onChange={(v) => upd("home", "hero_headline_2", v)} testid="cms-h2" />
          </div>
          <Textarea label="Subtext" value={content.home?.hero_subtext || ""} onChange={(v) => upd("home", "hero_subtext", v)} />
          <ImageField label="Hero image" value={content.home?.hero_image_url || ""} onChange={(v) => upd("home", "hero_image_url", v)} testid="cms-hero-img" />
        </Section>

        <Section title="STATS BAR">
          <div className="grid grid-cols-3 gap-3">
            <Input label="Members number" value={content.home?.stats_members || ""} onChange={(v) => upd("home", "stats_members", v)} />
            <Input label="Cities number" value={content.home?.stats_cities || ""} onChange={(v) => upd("home", "stats_cities", v)} />
            <Input label="Stores number" value={content.home?.stats_stores || ""} onChange={(v) => upd("home", "stats_stores", v)} />
          </div>
        </Section>

        <Section title="EDITORIAL VIP SECTION">
          <Input label="Eyebrow" value={content.home?.editorial_eyebrow || ""} onChange={(v) => upd("home", "editorial_eyebrow", v)} />
          <Input label="Headline" value={content.home?.editorial_headline || ""} onChange={(v) => upd("home", "editorial_headline", v)} />
          <Textarea label="Body" value={content.home?.editorial_body || ""} onChange={(v) => upd("home", "editorial_body", v)} />
          <ImageField label="Image" value={content.home?.editorial_image_url || ""} onChange={(v) => upd("home", "editorial_image_url", v)} />
        </Section>

        <Section title="FINAL CTA">
          <Input label="Headline" value={content.home?.final_cta_headline || ""} onChange={(v) => upd("home", "final_cta_headline", v)} />
          <Input label="Body" value={content.home?.final_cta_body || ""} onChange={(v) => upd("home", "final_cta_body", v)} />
          <ImageField label="Background image" value={content.home?.boutique_image_url || ""} onChange={(v) => upd("home", "boutique_image_url", v)} />
        </Section>

        <Section title="FOOTER">
          <Textarea label="Tagline" value={content.footer?.tagline || ""} onChange={(v) => upd("footer", "tagline", v)} />
          <Input label="Powered by line" value={content.footer?.powered_by || ""} onChange={(v) => upd("footer", "powered_by", v)} />
        </Section>

        <Section title="CUSTOMER SUPPORT INFO">
          <Input label="Email" value={content.support?.email || ""} onChange={(v) => upd("support", "email", v)} />
          <Input label="Phone" value={content.support?.phone || ""} onChange={(v) => upd("support", "phone", v)} />
          <Input label="Phone hours" value={content.support?.phone_hours || ""} onChange={(v) => upd("support", "phone_hours", v)} />
          <Input label="Address" value={content.support?.address || ""} onChange={(v) => upd("support", "address", v)} />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-black/10 p-5 space-y-3">
      <div className="text-[11px] uppercase tracking-[0.2em] kazo-text-burgundy mb-1">{title}</div>
      {children}
    </div>
  );
}
function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">{label}</label>
      <input className="k-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function Textarea({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">{label}</label>
      <textarea rows={3} className="k-input" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">{label}</label>
      <div className="flex gap-2 items-center">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 p-0 border border-black/10 rounded cursor-pointer" data-testid={testid} />
        <input className="k-input flex-1 font-mono text-xs" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}
function FontSelect({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">{label}</label>
      <select className="k-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
    </div>
  );
}
async function uploadImage(file, onChange) {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const r = await api.post("/cms/upload", fd);
    const base = process.env.REACT_APP_BACKEND_URL || "";
    onChange(base + r.data.url);
    toast.success("Image uploaded");
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Upload failed");
  }
}
function ImageField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">{label}</label>
      <div className="flex gap-3 items-start">
        <input className="k-input flex-1" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="Paste URL or upload →" />
        <label className="k-btn k-btn-outline k-btn-sm cursor-pointer whitespace-nowrap" data-testid={testid ? `${testid}-upload` : undefined}>
          <Upload className="w-4 h-4" /> Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, onChange); }} />
        </label>
        {value && <img src={value} alt="" className="w-16 h-16 object-contain border border-black/10 bg-neutral-50" />}
      </div>
    </div>
  );
}
